import { makeAutoObservable, runInAction } from 'mobx';
import { ICreateOrderData, IOrder, IOrderResponse } from '../types/entities';
import { $api } from '../http';


export default class OrderStore {
    _isLoading = false;
    _orders: IOrder[] = [];
    _error: string | null = null;
    _deliveryMethod: 'delivery' | 'takeaway' | 'dine_in' = 'delivery'; // Default to 'delivery'
    _isViewingArchived = false;

    constructor() {
        makeAutoObservable(this);
    }

    private _isFetching = false;

    // Actions
    setLoading(loading: boolean) {
        this._isLoading = loading;
    }

    setError(error: string | null) {
        this._error = error;
    }

    setOrders(orders: IOrder[]) {
        this._orders = orders;
    }

    setDeliveryMethod(method: 'delivery' | 'takeaway' | 'dine_in') {
        this._deliveryMethod = method;
    }

    setViewArchived(isArchived: boolean) {
        this._isViewingArchived = isArchived;
        this.fetchOrders();
    }
  
    // Getters
    get isLoading() {
        return this._isLoading;
    }

    get error() {
        return this._error;
    }

    get orders() {
        return this._orders;
    }

    get deliveryMethod() {
        return this._deliveryMethod;
    }

    get isViewingArchived() {
        return this._isViewingArchived;
    }

    // Async Actions
    async createOrder(orderData: ICreateOrderData): Promise<IOrderResponse> {
        this.setLoading(true);
        this.setError(null);
        try {
            const { data } = await $api.post<IOrderResponse>('/orders', orderData);
            return data;
        } catch (e: any) {
            runInAction(() => {
                this.setError(e.response?.data?.message || 'Wystąpił błąd podczas tworzenia zamówienia.');
            });
            throw e; // Re-throw to be caught in the component
        } finally {
            runInAction(() => {
                this.setLoading(false);
            });
        }
    }

    async fetchOrders() {
        if (this._isFetching) return;

        this._isFetching = true;
        this.setLoading(true);
        this.setError(null);
        try {
            const params: { timestamp: number; status?: string } = {
                timestamp: Date.now()
            };
            if (this._isViewingArchived) {
                params.status = 'archived';
            }
            // EN: Add a cache-busting parameter to ensure fresh data is always fetched.
            // PL: Dodaj parametr zapobiegający buforowaniu, aby zawsze pobierać świeże dane.
            const { data } = await $api.get<IOrder[]>('/orders', { params });

            runInAction(() => {
                this.setOrders(data);
            });
        } catch (e: any) {
            runInAction(() => {
                this.setError(e.response?.data?.message || 'Nie udało się pobrać zamówień.');
            });
        } finally {
            runInAction(() => {
                this.setLoading(false);
                this._isFetching = false;
            });
        }
    }

    async deleteOrder(orderId: number) {
        this.setLoading(true);
        this.setError(null);
        const originalOrders = [...this._orders];
        // EN: Optimistically remove the order from the list for a faster UI response.
        // PL: Optymistycznie usuń zamówienie z listy, aby uzyskać szybszą odpowiedź interfejsu użytkownika.
        runInAction(() => {
            this._orders = this._orders.filter((order) => order.id !== orderId);
        });
        try {
            await $api.delete(`/orders/${orderId}`);
        } catch (e: any) {
            const message = e.response?.data?.message || "Nie udało się usunąć zamówienia.";
            runInAction(() => {
                this.setError(message);
                this.setOrders(originalOrders); // EN: Rollback on failure / PL: Wycofaj zmiany w przypadku błędu
            });
            console.error("Błąd podczas usuwania zamówienia:", e);
        } finally {
            runInAction(() => this.setLoading(false));
        }
    }

    async archiveAllOrders() {
        this.setLoading(true);
        this.setError(null);
        try {
            await $api.post("/orders/archive-all");
            // EN: Refetch orders to see the updated status.
            // PL: Pobierz zamówienia ponownie, aby zobaczyć zaktualizowany status.
            await this.fetchOrders();
        } catch (e: any) {
            const message =
            e.response?.data?.message || "Nie udało się zarchiwizować zamówień.";
            runInAction(() => this.setError(message));
            console.error("Błąd podczas archiwizacji zamówień:", e);
        } finally {
            runInAction(() => this.setLoading(false));
        }
    }

    async deleteAllOrders() {
        this.setLoading(true);
        this.setError(null);
        try {
            await $api.delete("/orders/delete-all", {
                params: { status: this._isViewingArchived ? "archived" : "active" },
            });
            this.setOrders([]);
        } catch (e: any) {
            const message = e.response?.data?.message || "Nie udało się usunąć wszystkich zamówień.";
            runInAction(() => this.setError(message));
            console.error("Błąd podczas usuwania wszystkich zamówień:", e);
        } finally {
            runInAction(() => this.setLoading(false));
        }
    }
}
